import { fetchSession, postLogin, postLogout, postRenew } from "../api";
import { LoginFormContext } from "../components/LoginFormCtx";
import { authKeys } from "../keys";
import { type LoginFlowReturn, type SessionView } from "../types";
import { useMutation, useQuery, useQueryClient } from "@/lib/query";
import { makeInvalidator } from "@/lib/query/invalidate";
import { useContext } from "react";

// §6.2 — mutation wrapper for POST /auth/login.
// On success: invalidates the session query so RequireAuth re-evaluates.
// Usage: `const login = useLogin(); login.mutate({ email, password })`

interface LoginVars {
  email: string;
  password: string;
}

export function useLogin(): ReturnType<
  typeof useMutation<unknown, Error, LoginVars>
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ email, password }: LoginVars) => postLogin(email, password),
    onSuccess: () => queryClient.clear(),
  });
}

/** Consume the login-form context set up by LoginFormProvider.
 * Example: const { email, handleEmailSubmit } = useLoginFormContext();
 * Throws if called outside a LoginFormProvider tree.
 */

export function useLoginFormContext(): LoginFlowReturn {
  const ctx = useContext(LoginFormContext);
  if (!ctx)
    throw new Error(
      "useLoginFormContext must be used within LoginFormProvider",
    );
  return ctx;
}

// §6.2 — mutation wrapper for POST /auth/logout.
// On success: clears the session cache and all queries (stale data should not linger).
// Usage: `const logout = useLogout(); logout.mutate()`

export function useLogout(): ReturnType<
  typeof useMutation<unknown, Error, void>
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: postLogout,
    onSuccess: () => {
      queryClient.clear();
    },
  });
}

// §6.2 — mutation wrapper for POST /auth/renew (silent refresh).
// Called by the 401 middleware in client.ts; not meant for direct page use.
// Usage: `const renew = useRenew(); await renew.mutateAsync()`

export function useRenew(): ReturnType<
  typeof useMutation<unknown, Error, void>
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: postRenew,
    onSuccess: makeInvalidator(queryClient, authKeys.session()),
  });
}

/** Hook version of RequireAuth — returns { ok, isLoading } for programmatic checks.
 * Example: const { ok } = useRequireAuth(); if (!ok) return null;
 */

export function useRequireAuth(): { ok: boolean; isLoading: boolean } {
  const { isAuthenticated, isLoading } = useSession();
  return { ok: isAuthenticated, isLoading };
}

// §6.2 — useSession wraps GET /auth/session in TanStack Query.
// Session data stays in the query cache; never in Zustand.
// Usage: `const { data: session, isLoading } = useSession()`

export function useSession(): {
  session: SessionView | undefined;
  isLoading: boolean;
  isAuthenticated: boolean;
} {
  const query = useQuery({
    queryKey: authKeys.session(),
    queryFn: fetchSession,
    retry: false,
    // Session is core infra; stale immediately so re-fetches on mount catch expiry.
    staleTime: 0,
  });

  return {
    session: query.data,
    isLoading: query.isLoading,
    isAuthenticated: query.data !== undefined && !query.isError,
  };
}
